import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface FieldProps {
  /** Must match the input's id so clicking the label focuses the field. */
  htmlFor: string;
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
  className?: string;
}

/**
 * Label + control + hint/error, wired together for screen readers.
 *
 * The error replaces the hint rather than stacking below it, so the row never
 * changes height and the form does not jump as the user types.
 */
export function Field({ htmlFor, label, hint, error, children, className }: FieldProps) {
  const describedBy = error ? `${htmlFor}-error` : hint ? `${htmlFor}-hint` : undefined;

  return (
    <div className={cn('space-y-2', className)}>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-foreground">
        {label}
      </label>
      <div aria-describedby={describedBy}>{children}</div>
      {error ? (
        <p id={`${htmlFor}-error`} role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${htmlFor}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
